import { GPS, TrapSite } from "../../../types";
import { EditorDevice, StagedMoves } from "./types";

export interface Ghost {
  key: string;
  gps: GPS;
  /** What used to be here, so the ghost can say so. */
  label?: string;
}

const moved = (a?: GPS | null, b?: GPS | null): boolean =>
  !!a && !!b && (a.lat !== b.lat || a.lon !== b.lon);

/**
 * Where each thing on screen sat before it was dragged.
 *
 * Only positions that actually differ, so an untouched pin gets no ghost.
 * Derived from the stored records rather than tracked, which means saving -
 * and the refetch that follows - clears them with no extra bookkeeping.
 */
export const computeGhosts = (input: {
  devices: EditorDevice[];
  trapSites: Partial<TrapSite>[];
  staged: StagedMoves;
  /** The trap being edited, if any, and where its pin now sits. */
  editedDeviceUuid?: string | null;
  editedDeviceGps?: GPS | null;
  /** The location being edited, if any, and where its pin now sits. */
  editedSiteId?: string | null;
  editedSiteGps?: GPS | null;
}): Ghost[] => {
  const out: Ghost[] = [];
  const seen = new Set<string>();

  const push = (key: string, gps?: GPS | null, label?: string) => {
    if (!gps || seen.has(key)) return;
    seen.add(key);
    out.push({ key, gps, label });
  };

  if (input.editedDeviceUuid) {
    const device = input.devices.find((d) => d.uuid === input.editedDeviceUuid);
    if (moved(device?.gps, input.editedDeviceGps)) {
      push(
        `device-${input.editedDeviceUuid}`,
        device?.gps,
        device?.displayName,
      );
    }
  }

  if (input.editedSiteId) {
    const site = input.trapSites.find((t) => t._id === input.editedSiteId);
    if (moved(site?.gps, input.editedSiteGps)) {
      push(`site-${input.editedSiteId}`, site?.gps, site?.display_name);
    }
  }

  for (const [uuid, gps] of Object.entries(input.staged.devices)) {
    const device = input.devices.find((d) => d.uuid === uuid);
    if (moved(device?.gps, gps)) {
      push(`device-${uuid}`, device?.gps, device?.displayName);
    }
  }
  for (const [id, gps] of Object.entries(input.staged.trapSites)) {
    const site = input.trapSites.find((t) => t._id === id);
    if (moved(site?.gps, gps))
      push(`site-${id}`, site?.gps, site?.display_name);
  }

  return out;
};
